import axios from 'axios';
import { ax } from '@/services/http';
import List from './list';

// Backend response structure
export interface AgentResponse {
  id: string;
  name: string;
  description: string;
  avatar?: string;
  category?: string;
  isRecommended?: boolean;  // Backend uses camelCase
  isFavorite?: boolean;     // Backend uses camelCase
  creator?: string;
  created_at?: string;
  updated_at?: string;
}

// API response wrapper
interface AgentsApiResponse {
  agents: AgentResponse[];
}

export default class AgentsCollection extends List<AgentResponse> {

  /**
   * Fetch all available agents from the backend
   * Backend only provides list agents API
   *
   * Note: If you get CORS errors (OPTIONS 405), your backend needs to handle CORS:
   * 1. Add CORS middleware to handle OPTIONS requests
   * 2. Set proper CORS headers: Access-Control-Allow-Origin, Access-Control-Allow-Headers, etc.
   * 3. Ensure OPTIONS method is allowed for /agents endpoint
   */
  public async getAgents(params?: any): Promise<AgentResponse[]> {
    try {
      const token = this.getAuthToken();

      // Use dedicated Atlas API URL for agents, fallback to localhost:8000
      const baseURL = process.env.VUE_APP_ATLAS_API_URL || 'http://localhost:8000';
      const fullURL = `${baseURL}/agents`;

      // Create a simple request without triggering CORS preflight
      const response = await axios.get<AgentsApiResponse>(fullURL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params || {},
        timeout: 10000,
      });
      // Extract agents from the nested response structure
      const agents: AgentResponse[] = response.data?.agents || [];

      // Clear existing items and add new ones
      this.items = [];
      for (const agent of agents) {
        this.add(agent);
      }

      return agents;
    } catch (error: any) {
      // Provide more detailed error information
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.statusText;
        throw new Error(`API Error: ${status} - ${message}`);
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the agents API');
      } else {
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }
  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string {
    return localStorage.getItem('token') || 'test-token';
  }
}
